import { NetworkError } from './errors.ts';
import { getPref, PREF_KEYS } from '../db/prefs.ts';
import {
  recordSourceResponse,
  scheduleSourceRequest,
  sourceScopeFromUrl,
} from './source-scheduler.ts';

/**
 * Public base URL of the Cloudflare worker that fronts source metadata
 * requests. Configurable via `VITE_PROXY_BASE`; in dev defaults to the
 * `wrangler dev` port. Most image bytes never flow through this; Hitomi is
 * the exception because its CDN requires worker-synthesized Referer headers.
 */
export const PROXY_BASE =
  // Note: optional chaining on `import.meta.env` lets this module be
  // imported under raw Node ESM (e.g. by tests) without throwing —
  // Vite-injected envs only exist at build time, so under Node
  // `import.meta.env` is `undefined`.
  (import.meta.env?.VITE_PROXY_BASE as string | undefined)?.replace(/\/$/, '') ??
  // In Vite dev, use same-origin routes and let vite.config.ts proxy them
  // to the local worker. This keeps cloud/port-preview browsers from trying
  // to fetch their own `localhost:8787`.
  (import.meta.env?.DEV ? '' : undefined) ??
  'http://localhost:8787';

let proxyBasePromise: Promise<string> | undefined;

const DEFAULT_PROXY_TIMEOUT_MS = 20_000;

export async function getProxyBase(): Promise<string> {
  proxyBasePromise ??= getPref<string>(PREF_KEYS.PROXY_BASE)
    .then((saved) => {
      const trimmed = saved?.trim();
      return trimmed ? trimmed.replace(/\/$/, '') : PROXY_BASE;
    })
    .catch(() => PROXY_BASE);
  return proxyBasePromise;
}

export interface ProxyFetchOpts {
  method?: string;
  headers?: HeadersInit;
  body?: BodyInit | null;
  /**
   * A pre-serialized `name1=value1; name2=value2` cookie string. The worker
   * places this in the outbound `Cookie:` header. Source cookies never
   * touch our own origin.
   */
  cookies?: string;
  signal?: AbortSignal;
  timeoutMs?: number;
  /** Use the user's browser IP after the worker validates an anonymous read target. */
  transport?: 'browser';
  /** Optional debug label, recorded in console for parse-error capture. */
  debugLabel?: string;
}

/**
 * Fetch through the worker proxy.
 *
 * The proxy:
 *   1. enforces a hostname allow-list
 *   2. translates `X-Forward-Cookie` -> outbound `Cookie`
 *   3. sets a realistic Chrome User-Agent
 *   4. strips response `Set-Cookie` headers
 *   5. adds CORS headers
 *
 * Browsers cannot set a custom `Cookie:` header from JS to a third-party
 * origin anyway, hence the `X-Forward-Cookie` indirection.
 */
/**
 * Build the proxied URL for `url` without making a request. Useful for
 * `<img src>` tags that need to go through the worker so it can set a
 * specific Referer (hitomi's image CDNs hot-link-protect against any
 * non-hitomi.la referer, for example).
 *
 * Cross-origin `<img>` loads are normally a free win (browsers don't
 * apply CORS), so prefer the source's direct CDN URL where possible —
 * only wrap with `proxiedImageUrl` for hosts that need server-side
 * Referer override.
 */
export function proxiedImageUrl(url: string): string {
  return `${PROXY_BASE}/forward?u=${encodeURIComponent(url)}`;
}

export async function proxyFetch(url: string, opts: ProxyFetchOpts = {}): Promise<Response> {
  const scope = sourceScopeFromUrl(url);
  return scheduleSourceRequest(
    scope,
    async () => {
      const res = await doProxyFetch(url, opts);
      recordSourceResponse(scope, res);
      if (res.status >= 500 && res.status !== 509) {
        throw new NetworkError(`proxyFetch ${res.status} for ${url}`, { status: res.status });
      }
      return res;
    },
    opts.signal ? { signal: opts.signal } : {},
  );
}

async function doProxyFetch(url: string, opts: ProxyFetchOpts): Promise<Response> {
  const method = (opts.method ?? 'GET').toUpperCase();
  const headers = new Headers(opts.headers ?? {});
  if (opts.transport === 'browser') {
    const browserTarget = new URL(url);
    if (browserTarget.protocol !== 'https:' || (method !== 'GET' && method !== 'HEAD')) {
      throw new NetworkError('browser transport only supports HTTPS GET or HEAD requests');
    }
    if (
      (opts.body !== undefined && opts.body !== null) ||
      opts.cookies !== undefined ||
      headers.has('X-Forward-Cookie') ||
      headers.has('X-Forward-Headers')
    ) {
      throw new NetworkError('browser transport cannot forward a body, cookies, or plugin headers');
    }
  }

  const proxyBase = await getProxyBase();
  const transport = opts.transport === 'browser' ? '&transport=browser' : '';
  const target = `${proxyBase}/forward?u=${encodeURIComponent(url)}${transport}`;
  const timeoutMs = opts.timeoutMs ?? DEFAULT_PROXY_TIMEOUT_MS;
  const timeoutController =
    !opts.signal && timeoutMs > 0 ? new AbortController() : undefined;
  const timeoutId =
    timeoutController !== undefined
      ? globalThis.setTimeout(() => timeoutController.abort(), timeoutMs)
      : undefined;
  try {
    if (opts.cookies) {
      headers.set('X-Forward-Cookie', opts.cookies);
    }
    const init: RequestInit = {
      method,
      headers,
    };
    if (opts.body !== undefined && opts.body !== null) init.body = opts.body;
    if (opts.signal) init.signal = opts.signal;
    else if (timeoutController) init.signal = timeoutController.signal;

    return await fetch(target, init);
  } catch (err: unknown) {
    if (err instanceof NetworkError) throw err;
    const msg = err instanceof Error ? err.message : String(err);
    throw new NetworkError(`proxyFetch failed for ${url}: ${msg}`, { cause: err });
  } finally {
    if (timeoutId !== undefined) globalThis.clearTimeout(timeoutId);
  }
}
